import {
  RETAILER_LABELS,
  RETAILERS,
  STATUS_LABELS,
  type Address,
  type OrderDetail,
  type OrderStatus,
} from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useCanGoBack, useRouter } from '@tanstack/react-router'
import { ArrowLeft, Check, ExternalLink, Truck, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { CopyButton } from '@/components/app/copy-button'
import { ProductThumb, RetailerMark } from '@/components/app/retailer'
import { ErrorState } from '@/components/app/states'
import { StatusBadge } from '@/components/app/status-badge'
import { PanelHeader, RowChevron, Surface } from '@/components/app/surface'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { trackingUrl } from '@/lib/carriers'
import { ago, calendarDay, count, dateTime, money } from '@/lib/format'
import { orderQuery, ordersQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/orders/$orderNumber')({
  validateSearch: z.object({ retailer: z.enum(RETAILERS).optional() }),
  loaderDeps: ({ search }) => ({ retailer: search.retailer }),
  loader: ({ context, params, deps }) => context.queryClient.prefetchQuery(orderQuery(params.orderNumber, deps.retailer)),
  head: ({ params }) => ({ meta: [{ title: `Order ${params.orderNumber} · Nebueye` }] }),
  component: OrderPage,
})

function OrderPage() {
  const { orderNumber } = Route.useParams()
  const { retailer } = Route.useSearch()
  const q = useQuery(orderQuery(orderNumber, retailer))

  return (
    <div className="space-y-6">
      <BackLink />
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !q.data ? (
        <OrderSkeleton />
      ) : (
        <OrderView o={q.data} />
      )}
    </div>
  )
}

/** Back to wherever the user came from (keeps their filters), else Orders. */
function BackLink() {
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const cls = 'text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm'
  return canGoBack ? (
    <button type="button" onClick={() => router.history.back()} className={cls}>
      <ArrowLeft className="size-4" /> Back
    </button>
  ) : (
    <Link to="/orders" className={cls}>
      <ArrowLeft className="size-4" /> Orders
    </Link>
  )
}

function OrderView({ o }: { o: OrderDetail }) {
  const units = o.items.reduce((s, i) => s + i.quantity, 0)
  const lead = o.items[0]
  const title = o.items.length > 1 ? `${lead?.name} + ${o.items.length - 1} more` : (lead?.name ?? `Order ${o.orderNumber}`)

  return (
    <>
      <header className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <ProductThumb src={lead?.imageUrl} retailer={o.retailer} className="size-24 rounded-xl text-xl sm:size-28" eager />
        <div className="min-w-0 flex-1 space-y-3">
          <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-sm">
            <span>{RETAILER_LABELS[o.retailer]}</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center">
              <span className="id-mono text-foreground">#{o.orderNumber}</span>
              <CopyButton value={o.orderNumber} label="Copy order number" />
            </span>
            <span aria-hidden>·</span>
            <span>Placed {dateTime(o.orderDate)}</span>
          </p>
          <h1 className="text-headline font-semibold text-balance">{title}</h1>
          {o.items.length === 1 && lead && (
            <p className="text-muted-foreground tabular flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span>
                {count(lead.quantity)} × {money(lead.price)}
              </span>
              {lead.sku && <span className="id-mono text-subtle">SKU {lead.sku}</span>}
              {lead.productLink && (
                <a href={lead.productLink} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 font-medium hover:underline">
                  View at {RETAILER_LABELS[o.retailer]} <ExternalLink className="size-3.5" />
                </a>
              )}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <StatusBadge status={o.status} />
            {o.cancelReason && <span className="text-status-danger-text text-sm">{o.cancelReason}</span>}
            {o.statusManual && <span className="text-subtle text-xs">Status set by hand</span>}
            <span className="text-subtle text-xs">Updated {ago(o.updatedAt)}</span>
          </div>
        </div>
        {o.trackingNumber && (
          <Button asChild className="shrink-0">
            <a href={trackingUrl(o.carrier, o.trackingNumber)} target="_blank" rel="noreferrer">
              <Truck /> Track package
            </a>
          </Button>
        )}
      </header>

      <Surface className="p-5 sm:p-6">
        <Progress o={o} />
      </Surface>

      <div className="bg-border grid grid-cols-[minmax(0,1fr)] gap-px overflow-hidden rounded-xl border shadow-xs sm:grid-cols-2 xl:grid-cols-4">
          <Section title="Summary" className="bg-card p-5">
            <dl className="tabular space-y-2 text-sm">
              <Row label={`Subtotal · ${count(units)} ${units === 1 ? 'unit' : 'units'}`} value={money(o.subtotal)} />
              <Row label="Tax" value={money(o.tax)} />
              <div className="border-t pt-2">
                <Row label="Total" value={money(o.total)} strong />
              </div>
            </dl>
          </Section>

          <Section title="Delivery" className="bg-card p-5">
            <dl className="space-y-3 text-sm">
              {o.trackingNumber ? (
                <Detail label={o.carrier ?? 'Tracking'}>
                  <span className="inline-flex items-center">
                    <a href={trackingUrl(o.carrier, o.trackingNumber)} target="_blank" rel="noreferrer" className="id-mono text-primary break-all hover:underline">
                      {o.trackingNumber}
                    </a>
                    <CopyButton value={o.trackingNumber} label="Copy tracking number" />
                  </span>
                </Detail>
              ) : (
                <Detail label="Tracking">
                  <span className="text-muted-foreground">{o.status === 'cancelled' ? 'Not shipped' : 'Not shipped yet'}</span>
                </Detail>
              )}
              {o.deliveredAt ? (
                <Detail label="Delivered">{dateTime(o.deliveredAt)}</Detail>
              ) : o.deliveryStart ? (
                <Detail label="Expected">
                  {calendarDay(o.deliveryStart)}
                  {o.deliveryEnd && o.deliveryEnd !== o.deliveryStart && ` – ${calendarDay(o.deliveryEnd)}`}
                </Detail>
              ) : o.deliveryDate ? (
                <Detail label="Expected">{o.deliveryDate}</Detail>
              ) : null}
              {o.address && <Detail label="Ship to">{formatAddress(o.address)}</Detail>}
            </dl>
          </Section>

          <Section title="Payment" className="bg-card p-5">
            <dl className="space-y-3 text-sm">
              <Detail label="Card">
                {o.cardType || o.cardLastFour ? (
                  <>
                    {o.cardType ?? 'Card'} {o.cardLastFour && <span className="tabular">•••• {o.cardLastFour}</span>}
                  </>
                ) : (
                  <span className="text-muted-foreground">Not stated in the email</span>
                )}
              </Detail>
              {o.billing && <Detail label="Billing">{formatAddress(o.billing)}</Detail>}
            </dl>
          </Section>

          <Section title="Account" className="bg-card p-5">
            <dl className="space-y-3 text-sm">
              <Detail label="Email">
                <Link to="/orders" search={{ search: o.email }} className="text-primary break-all hover:underline">
                  {o.email}
                </Link>
              </Detail>
              {o.sourceMailbox && <Detail label="Inbox">{o.sourceMailbox}</Detail>}
            </dl>
          </Section>
      </div>

      <div className="space-y-6">
          {o.items.length > 1 && (
          <Surface>
            <PanelHeader title="Items" description={`${count(units)} units`} />
            <ul className="divide-y border-t">
              {o.items.map((i) => (
                <li key={i.id} className="flex gap-4 px-5 py-4">
                  <ProductThumb src={i.imageUrl} retailer={o.retailer} className="size-16 rounded-lg" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="font-medium">{i.name}</p>
                    <p className="text-muted-foreground tabular text-[13px]">
                      {count(i.quantity)} × {money(i.price)}
                      {i.sku && <span className="id-mono text-subtle ml-3">SKU {i.sku}</span>}
                    </p>
                    {i.productLink && (
                      <a href={i.productLink} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 text-[13px] font-medium hover:underline">
                        View at {RETAILER_LABELS[o.retailer]} <ExternalLink className="size-3.5" />
                      </a>
                    )}
                  </div>
                  <p className="tabular shrink-0 font-semibold">{money(i.price * i.quantity)}</p>
                </li>
              ))}
            </ul>
          </Surface>
          )}

          {o.discordAlerts.length > 0 && (
            <Surface>
              <PanelHeader title="Checkout bot" description="What your bot posted about this order" />
              <ul className="divide-y border-t">
                {o.discordAlerts.map((a) => (
                  <li key={a.id} className="flex items-start justify-between gap-4 px-5 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium capitalize">
                        {a.kind}
                        {a.reason && <span className="text-muted-foreground font-normal normal-case"> · {a.reason}</span>}
                      </p>
                      <p className="text-muted-foreground text-[13px]">
                        {a.botLabel}
                        {a.profile && <> · profile {a.profile}</>}
                        {a.quantity > 0 && <> · qty {a.quantity}</>}
                      </p>
                    </div>
                    <p className="text-muted-foreground tabular shrink-0 text-[13px]">{dateTime(a.postedAt)}</p>
                  </li>
                ))}
              </ul>
            </Surface>
          )}

          <MoreFromAccount o={o} />
      </div>
    </>
  )
}

/** Where the order is on its way: placed → shipped → out for delivery → delivered. */
function Progress({ o }: { o: OrderDetail }) {
  const at = (s: OrderStatus) => o.statusHistory.find((e) => e.status === s)?.occurredAt
  const cancelled = o.status === 'cancelled' || o.status === 'refunded'
  const pickup = o.statusHistory.some((e) => e.status === 'ready_for_pickup')
  const steps: { label: string; when?: string; done: boolean }[] = cancelled
    ? [
        { label: 'Placed', when: o.orderDate, done: true },
        { label: STATUS_LABELS[o.status], when: at(o.status), done: true },
      ]
    : [
        { label: 'Placed', when: o.orderDate, done: true },
        pickup
          ? { label: 'Ready for pickup', when: at('ready_for_pickup'), done: true }
          : { label: 'Shipped', when: at('shipped'), done: o.shipped || o.delivered },
        ...(pickup ? [] : [{ label: 'Out for delivery', when: at('out_for_delivery'), done: o.status === 'out_for_delivery' || o.delivered }]),
        { label: pickup ? 'Picked up' : 'Delivered', when: o.deliveredAt, done: o.delivered },
      ]
  const current = steps.findLastIndex((s) => s.done)
  // Events the tracker has no step for: hiccups and hand edits.
  const notes = o.statusHistory.filter((e) => e.manual || ['delayed', 'payment_failed', 'preorder'].includes(e.status))

  return (
    <div className="space-y-4">
    <ol className="grid gap-4 sm:grid-flow-col sm:auto-cols-fr sm:gap-0" aria-label="Order progress">
      {steps.map((s, i) => {
        const failed = cancelled && i === steps.length - 1
        return (
          <li key={s.label} className="relative flex gap-3 sm:flex-col sm:gap-2.5" aria-current={i === current ? 'step' : undefined}>
            <div className="flex items-center sm:w-full">
              <span
                className={cn(
                  'grid size-6 shrink-0 place-items-center rounded-full',
                  failed ? 'bg-status-danger text-white' : s.done ? 'bg-status-success text-white' : 'bg-surface-sunken text-subtle ring-border-strong ring-1',
                )}
              >
                {failed ? <X className="size-3.5" strokeWidth={3} /> : s.done ? <Check className="size-3.5" strokeWidth={3} /> : <span className="size-1.5 rounded-full bg-current" />}
              </span>
              {i < steps.length - 1 && (
                <span className={cn('mx-2 hidden h-0.5 flex-1 rounded-full sm:block', steps[i + 1]?.done ? (cancelled ? 'bg-status-danger/60' : 'bg-status-success') : 'bg-border')} />
              )}
            </div>
            <div>
              <p className={cn('text-sm', i === current ? 'font-semibold' : s.done ? 'font-medium' : 'text-muted-foreground')}>{s.label}</p>
              <p className="text-muted-foreground tabular text-[13px]">{s.when ? dateTime(s.when) : '—'}</p>
            </div>
          </li>
        )
      })}
    </ol>
    {notes.length > 0 && (
      <ul className="flex flex-wrap gap-2">
        {notes.map((e) => (
          <li key={e.id} className="bg-status-warning-soft text-status-warning-text rounded-full px-3 py-1 text-[13px]">
            {STATUS_LABELS[e.status]}
            {e.manual && ' (set by hand)'} · {dateTime(e.occurredAt)}
          </li>
        ))}
      </ul>
    )}
    </div>
  )
}

function MoreFromAccount({ o }: { o: OrderDetail }) {
  const q = useQuery(ordersQuery({ email: o.email, limit: 6, sort: 'placed', desc: 'true' }))
  const others = q.data?.orders.filter((x) => x.id !== o.id).slice(0, 5) ?? []
  if (others.length === 0) return null
  return (
    <Surface>
      <PanelHeader
        title="More orders to this email"
        description={o.email}
        action={
          <Link to="/orders" search={{ search: o.email }} className="text-primary text-[13px] font-medium hover:underline">
            See all
          </Link>
        }
      />
      <ul className="divide-y border-t">
        {others.map((x) => (
          <li key={x.id}>
            <Link
              to="/orders/$orderNumber"
              params={{ orderNumber: x.orderNumber }}
              search={{ retailer: x.retailer }}
              className="hover:bg-surface-sunken/60 group flex items-center gap-3.5 px-5 py-3 transition-colors"
            >
              <ProductThumb src={x.items[0]?.imageUrl} retailer={x.retailer} className="size-10 rounded-lg text-xs" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{x.items[0]?.name ?? `Order ${x.orderNumber}`}</p>
                <p className="text-muted-foreground text-[13px]">
                  <span className="id-mono">#{x.orderNumber}</span> · {ago(x.orderDate)}
                </p>
              </div>
              <StatusBadge status={x.status} className="hidden sm:inline-flex" />
              <span className="tabular w-20 text-right text-sm font-medium">{money(x.total)}</span>
              <RowChevron />
            </Link>
          </li>
        ))}
      </ul>
    </Surface>
  )
}

const formatAddress = (a: Address) =>
  [
    a.recipientName,
    [a.addressNumber, a.addressStreet].filter(Boolean).join(' '),
    a.addressLine2,
    [a.city, a.state].filter(Boolean).join(', ') + (a.zip ? ` ${a.zip}` : ''),
  ]
    .filter(Boolean)
    .join('\n')

function Section({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <section className={cn('space-y-3', className)}>
      <h2 className="text-[15px] font-semibold">{title}</h2>
      {children}
    </section>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between', strong ? 'text-base font-semibold' : 'text-muted-foreground')}>
      <dt>{label}</dt>
      <dd className={strong ? '' : 'text-foreground'}>{value}</dd>
    </div>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="whitespace-pre-line">{children}</dd>
    </div>
  )
}

function OrderSkeleton() {
  return (
    <div className="space-y-10" aria-busy="true" aria-label="Loading order">
      <div className="flex gap-6">
        <Skeleton className="size-28 rounded-xl" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-8 w-96 max-w-full" />
          <Skeleton className="h-6 w-28 rounded-full" />
        </div>
      </div>
      <Skeleton className="h-14 w-full" />
      <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr]">
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    </div>
  )
}
